import React, { useState, useRef, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { 
  Container, 
  Typography, 
  TextField, 
  Box, 
  Paper, 
  Fade,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';

const VALIDATE_URL =
  process.env.NODE_ENV === 'development'
    ? '/valida.php'
    : 'https://api.boifestmarilia.com.br/valida.php';
const REQUEST_TIMEOUT_MS = 10000;
const HISTORY_LIMIT = 10;

const formatInviteLabel = (inviteNumber, code) => {
  if (inviteNumber && code && inviteNumber !== code) {
    return `${inviteNumber} · ${code}`;
  }
  return inviteNumber || code || '';
};

const formatReadTime = (date) =>
  date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const Page = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: '#f4f1ea',
  padding: theme.spacing(4, 3, 5),
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4.5, 4),
  borderRadius: 24,
  background: '#141414',
  border: '1px solid #2a241c',
  maxWidth: 440,
  width: '100%',
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 14,
    backgroundColor: '#1c1c1c',
    '& fieldset': {
      borderColor: '#3a342c',
    },
    '&:hover fieldset': {
      borderColor: '#c47a2e',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#c47a2e',
      borderWidth: 1.5,
    },
  },
  '& .MuiInputBase-input': {
    color: '#f4f1ea',
    fontSize: '1.35rem',
    fontWeight: 500,
    textAlign: 'center',
    letterSpacing: '0.14em',
    padding: '18px 14px',
  },
  '& .MuiInputLabel-root': {
    color: '#9a9186',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#c47a2e',
  },
});

const ResultPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})(({ tone }) => {
  const palette = {
    success: { bg: 'rgba(46, 160, 90, 0.12)', border: '#2ea05a', text: '#7ddea3' },
    error: { bg: 'rgba(220, 70, 60, 0.12)', border: '#dc463c', text: '#f0a39d' },
    idle: { bg: 'rgba(196, 122, 46, 0.1)', border: '#c47a2e', text: '#e8c089' },
    alert: { bg: 'rgba(196, 122, 46, 0.16)', border: '#c47a2e', text: '#f0c57a' },
  };
  const colors = palette[tone] || palette.idle;
  return {
    marginTop: 20,
    padding: '16px 18px',
    borderRadius: 14,
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    color: colors.text,
    textAlign: 'center',
  };
});

const HistoryBox = styled(Box)({
  marginTop: 22,
  padding: '14px 16px 10px',
  borderRadius: 14,
  background: '#1c1c1c',
  border: '1px solid #2a241c',
});

const HistoryRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})(({ tone }) => {
  const colors = {
    success: '#7ddea3',
    error: '#f0a39d',
    alert: '#f0c57a',
  };
  return {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid #2a241c',
    '&:last-of-type': {
      borderBottom: 'none',
    },
    color: colors[tone] || colors.error,
  };
});

const extractInviteCode = (rawValue) => {
  const value = String(rawValue || '').replace(/[\u0000-\u001F]+/g, '').trim();
  if (!value) return '';

  const withoutQuery = value.split(/[?#]/)[0].replace(/\/+$/, '');
  const isUrl =
    /^https?:\/\//i.test(withoutQuery) || /boifestmarilia\.com\.br\//i.test(withoutQuery);

  if (!isUrl) return withoutQuery;

  const segments = withoutQuery.split('/').filter(Boolean);
  const last = segments[segments.length - 1] || '';
  if (!last || last.includes('.')) return withoutQuery;
  return last;
};

const isTruthyFlag = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
};

const parseAlertText = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const parseInviteResponse = (data) => {
  let isValidResponse = false;
  let messageFromApi = '';
  let inviteNumber = '';
  let alertText = '';

  if (data) {
    if (typeof data === 'object') {
      if (data.msg !== undefined) {
        messageFromApi = data.msg;
      }
      alertText = parseAlertText(data.alerta);
      if (data.code !== undefined && data.code !== null && String(data.code).trim() !== '') {
        inviteNumber = String(data.code).trim();
      }

      const hasValid = data.isValid !== undefined;
      const hasSuccess = data.success !== undefined;
      const hasValidField = data.valid !== undefined;

      if (hasValid) {
        isValidResponse = isTruthyFlag(data.isValid);
      } else if (hasSuccess) {
        isValidResponse = isTruthyFlag(data.success);
      } else if (hasValidField) {
        isValidResponse = isTruthyFlag(data.valid);
      } else {
        isValidResponse = false;
      }
    } else if (typeof data === 'string') {
      const responseText = data.toLowerCase();
      const hasValid = responseText.includes('valid');
      const hasSuccess = responseText.includes('success');
      const hasTrue = responseText.includes('true');
      const hasOk = responseText.includes('ok');
      const hasInvalid = responseText.includes('invalid');
      const hasError = responseText.includes('error');
      const hasFalse = responseText.includes('false');
      const hasFail = responseText.includes('fail');

      if (hasInvalid || hasError || hasFalse || hasFail) {
        isValidResponse = false;
      } else if (hasValid || hasSuccess || hasTrue || hasOk) {
        isValidResponse = true;
      } else {
        isValidResponse = false;
      }
    }
  }

  return { isValidResponse, messageFromApi, inviteNumber, alertText };
};

const QrScannerComponent = () => {
  const [isValid, setIsValid] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastValidatedCode, setLastValidatedCode] = useState('');
  const [lastInviteNumber, setLastInviteNumber] = useState('');
  const [apiMessage, setApiMessage] = useState('');
  const [alertText, setAlertText] = useState('');
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const codeRef = useRef('');
  const validatingRef = useRef(false);
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  const setCodeValue = (next) => {
    codeRef.current = next;
    if (inputRef.current) {
      inputRef.current.value = next;
    }
  };

  const finishReading = ({ ok, message, inviteNumber, code, alert }) => {
    const nextAlert = parseAlertText(alert);
    setIsValid(ok);
    setLastValidatedCode(code);
    setLastInviteNumber(inviteNumber || '');
    setApiMessage(message || '');
    setAlertText(nextAlert);
    setCodeValue('');
    setHistory((prev) =>
      [
        {
          id: `${Date.now()}-${code}`,
          ok,
          tone: nextAlert ? 'alert' : ok ? 'success' : 'error',
          label: formatInviteLabel(inviteNumber, code),
          message: message || (ok ? 'Convite validado com sucesso' : 'Convite inválido'),
          alert: nextAlert,
          at: formatReadTime(new Date()),
        },
        ...prev,
      ].slice(0, HISTORY_LIMIT)
    );
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  useEffect(() => {
    focusInput();
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      const input = inputRef.current;
      if (!input) return;
      if (document.activeElement === input) return;
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      if (event.key.length !== 1 && event.key !== 'Enter') return;

      event.preventDefault();
      input.focus();

      if (event.key.length === 1 && !validatingRef.current) {
        setCodeValue(codeRef.current + event.key);
        setIsValid(null);
        setApiMessage('');
        setAlertText('');
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  const handleCodeChange = (event) => {
    if (validatingRef.current) return;
    setCodeValue(event.target.value);
    if (isValid !== null) {
      setIsValid(null);
      setApiMessage('');
      setAlertText('');
    }
  };

  const validateQrCode = async (rawValue) => {
    const codeToValidate = extractInviteCode(rawValue);
    if (!codeToValidate || validatingRef.current) return;

    validatingRef.current = true;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setIsValid(null);
    setApiMessage('');
    setAlertText('');
    setLastInviteNumber('');

    const isCurrent = () => requestId === requestIdRef.current;

    try {
      const response = await axios.post(
        VALIDATE_URL,
        { code: codeToValidate },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: REQUEST_TIMEOUT_MS,
          signal: controller.signal,
        }
      );

      if (!isCurrent()) return;

      if (response.data === '' || response.data == null) {
        finishReading({
          ok: false,
          message: 'Convite inexistente',
          inviteNumber: '',
          code: codeToValidate,
        });
        return;
      }

      const { isValidResponse, messageFromApi, inviteNumber, alertText: nextAlert } = parseInviteResponse(response.data);
      finishReading({
        ok: isValidResponse,
        message: messageFromApi,
        inviteNumber,
        code: codeToValidate,
        alert: nextAlert,
      });
    } catch (err) {
      if (!isCurrent()) return;
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;

      const { inviteNumber, messageFromApi, alertText: nextAlert } = parseInviteResponse(err.response?.data);
      let message = messageFromApi;
      if (err.code === 'ECONNABORTED') {
        message = 'Tempo esgotado. Passe o convite de novo.';
      } else if (!err.response) {
        message = 'Sem conexão. Verifique a rede.';
      }

      finishReading({
        ok: false,
        message,
        inviteNumber,
        code: codeToValidate,
        alert: nextAlert,
      });
    } finally {
      if (isCurrent()) {
        validatingRef.current = false;
        setIsLoading(false);
        focusInput();
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const rawValue = inputRef.current?.value || codeRef.current;
    const value = extractInviteCode(rawValue);
    if (!value || validatingRef.current) return;
    validateQrCode(value);
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;
    window.__validateInvite = (code) => validateQrCode(code);
    return () => {
      delete window.__validateInvite;
    };
  });

  const getStatusMessage = () => {
    if (isLoading) return 'Validando...';
    if (apiMessage && apiMessage.trim() !== '') return apiMessage;
    if (isValid === true) return 'Convite Validado com Sucesso!';
    if (isValid === false) return 'Convite Inválido';
    return 'Aguardando leitura...';
  };

  return (
    <Page maxWidth="sm">
      <Card elevation={0}>
        <Box textAlign="center" mb={4}>
          <Typography
            variant="overline"
            sx={{
              color: '#c47a2e',
              letterSpacing: '0.28em',
              fontWeight: 600,
              display: 'block',
              mb: 1,
            }}
          >
            Validação de convites
          </Typography>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#f4f1ea',
              fontSize: { xs: '2rem', sm: '2.4rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}
          >
            Boi Fest{' '}
            <Box component="span" sx={{ color: '#c47a2e' }}>
              2026
            </Box>
          </Typography>
        </Box>

        <StyledTextField
          inputRef={inputRef}
          fullWidth
          label="Código do convite"
          defaultValue=""
          onChange={handleCodeChange}
          onKeyDown={handleKeyDown}
          placeholder="Escaneie ou digite e pressione Enter"
          variant="outlined"
          autoComplete="off"
          autoFocus
          inputProps={{
            autoCapitalize: 'off',
            autoCorrect: 'off',
            spellCheck: false,
          }}
        />

        {isLoading && (
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress size={22} sx={{ color: '#c47a2e' }} />
          </Box>
        )}

        <Fade in={isValid !== null || isLoading} unmountOnExit>
          <ResultPanel
            tone={alertText ? 'alert' : isValid === true ? 'success' : isValid === false ? 'error' : 'idle'}
          >
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.45rem', sm: '1.7rem' }, lineHeight: 1.25, color: 'inherit' }}>
              {getStatusMessage()}
            </Typography>
            {(lastInviteNumber || lastValidatedCode) && (
              <Typography
                sx={{
                  mt: 1.25,
                  fontSize: { xs: '1.35rem', sm: '1.55rem' },
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  color: 'inherit',
                  lineHeight: 1.3,
                }}
              >
                {formatInviteLabel(lastInviteNumber, lastValidatedCode)}
              </Typography>
            )}
            {alertText && (
              <Typography
                sx={{
                  mt: 1.25,
                  fontWeight: 700,
                  fontSize: { xs: '1.2rem', sm: '1.35rem' },
                  lineHeight: 1.3,
                  color: 'inherit',
                }}
              >
                {alertText}
              </Typography>
            )}
          </ResultPanel>
        </Fade>

        <Typography
          variant="body2"
          sx={{
            mt: 3.5,
            color: '#7a736a',
            fontSize: '0.85rem',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          Leitor USB no campo · Enter para validar
        </Typography>
        <Typography
          component={RouterLink}
          to="/dashboard"
          sx={{
            display: 'block',
            mt: 1.25,
            color: '#c47a2e',
            fontSize: '0.82rem',
            textAlign: 'center',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          Abrir painel
        </Typography>

        <HistoryBox>
          <Typography
            sx={{
              color: '#9a9186',
              fontSize: '0.72rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            Últimas leituras
          </Typography>
          {history.length === 0 ? (
            <Typography sx={{ color: '#7a736a', fontSize: '0.9rem', py: 1 }}>
              Nenhuma leitura nesta sessão
            </Typography>
          ) : (
            history.map((item) => (
              <HistoryRow key={item.id} tone={item.tone}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'inherit', lineHeight: 1.3 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'inherit', opacity: 0.8, mt: 0.2 }}>
                    {item.message}
                  </Typography>
                  {item.alert && (
                    <Typography sx={{ fontSize: '0.78rem', color: 'inherit', fontWeight: 700, mt: 0.2 }}>
                      {item.alert}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#9a9186', flexShrink: 0 }}>
                  {item.at}
                </Typography>
              </HistoryRow>
            ))
          )}
        </HistoryBox>
      </Card>
    </Page>
  );
};

export default QrScannerComponent;
